import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Table, Button, Breadcrumb, Alert, Modal, Form, Spinner, ProgressBar, Dropdown, DropdownButton } from 'react-bootstrap';
import axios from 'axios';
import { listFiles, getFileContent, saveFile, deleteFile } from '../services/api';

function FileBrowser({ staging = false }) {
  const target = staging ? 'staging' : 'live';
  const targetQuery = staging ? '?target=staging' : '';
  const fileBase = staging ? 'staging/files' : 'files';
  const { siteId, '*': path } = useParams();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [fileContent, setFileContent] = useState('');
  const [editingFile, setEditingFile] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [showNewFileModal, setShowNewFileModal] = useState(false);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [newFileName, setNewFileName] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ show: false, percent: 0, processed: 0, total: 0 });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  

  useEffect(() => {
    const normalizedPath = path ? path.replace(/^\/+|\/+$/g, '') : '';
    setCurrentPath(normalizedPath);
    fetchFiles(normalizedPath);
  }, [siteId, path, staging]);

  useEffect(() => {
    // Set up drag and drop event handlers
    const handleDragOver = (e) => {
      e.preventDefault();
      setIsDragging(true);
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      setIsDragging(false);
    };

    const handleDrop = (e) => {
      e.preventDefault();
      setIsDragging(false);
      
      if (e.dataTransfer.items) {
        handleDroppedItems(e.dataTransfer.items);
      } else if (e.dataTransfer.files) {
        handleFiles(Array.from(e.dataTransfer.files));
      }
    };

    const dropZone = dropZoneRef.current;
    if (dropZone) {
      dropZone.addEventListener('dragover', handleDragOver);
      dropZone.addEventListener('dragleave', handleDragLeave);
      dropZone.addEventListener('drop', handleDrop);
      
      return () => {
        dropZone.removeEventListener('dragover', handleDragOver);
        dropZone.removeEventListener('dragleave', handleDragLeave);
        dropZone.removeEventListener('drop', handleDrop);
      };
    }
  }, [dropZoneRef.current, currentPath]); // Added currentPath to dependencies

  const fetchFiles = async (dirPath) => {
    try {
      setLoading(true);
      const data = await listFiles(siteId, dirPath, false, target);
      if (data.success) {
        // Sort: directories first, then files
        const sortedFiles = [...data.files].sort((a, b) => {
          if (a.is_directory && !b.is_directory) return -1;
          if (!a.is_directory && b.is_directory) return 1;
          return a.name.localeCompare(b.name);
        });
        setFiles(sortedFiles);
      } else {
        setError(data.message || 'Failed to load files');
      }
    } catch (err) {
      setError('Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = (path) => {
    navigate(`/sites/${siteId}/${fileBase}/${path}`);
  };
  
  // Navigate back to site
  const navigateToSite = () => {
    navigate(`/sites/${siteId}${staging ? '/staging' : ''}`);
  };

  // Handle editing a file
  const handleEditFile = async (file) => {
    try {
      setContentLoading(true);
      setEditingFile(file);
      
      const response = await getFileContent(siteId, file.path, target);
      
      // Check if it's a binary/image file
      if (response.binary) {
        if (response.mime_type && response.mime_type.startsWith('image/')) {
          // Show image viewer instead
          setShowImageViewer(true);
        } else {
          setMessage({ 
            text: 'Binary files cannot be edited in the text editor', 
            type: 'warning' 
          });
        }
      } else {
        // It's a text file, show editor
        setFileContent(response.content || '');
        setShowEditModal(true);
      }
    } catch (err) {
      setMessage({ text: 'Failed to load file content', type: 'danger' });
    } finally {
      setContentLoading(false);
    }
  };

  const handleSaveFile = async () => {
    try {
      const response = await saveFile(siteId, editingFile.path, fileContent, target);
      if (response.success) {
        setMessage({ text: 'File saved successfully', type: 'success' });
        setShowEditModal(false);
        fetchFiles(currentPath);
      } else {
        setMessage({ text: response.message || 'Failed to save file', type: 'danger' });
      }
    } catch (err) {
      setMessage({ text: 'Failed to save file', type: 'danger' });
    }
  };

  // Handle file download programmatically to avoid URL fragment issues with # in filenames
  const handleDownload = async (file) => {
    try {
      const response = await axios.get(
        `/api/sites/${siteId}/download/${encodePath(file.path)}/${targetQuery}`,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', getFileName(file.path));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      setMessage({ text: `Failed to download ${getFileName(file.path)}`, type: 'danger' });
    }
  };

  const handleDownloadAll = () => {
    const params = new URLSearchParams({ path: currentPath || '', target });
    window.location.href = `/api/sites/${siteId}/download-folder/?${params}`;
  };

  const handleDownloadEntireSite = () => {
    window.location.href = `/api/sites/${siteId}/download-site/${targetQuery}`;
  };

  // Create new blank file
  const handleCreateFile = async () => {
    if (!newFileName.trim()) return;
    
    try {
      const filePath = currentPath 
        ? `${currentPath}/${newFileName}`
        : newFileName;
      
      const response = await saveFile(siteId, filePath, '', target);
      
      if (response.success) {
        setMessage({ text: 'File created successfully', type: 'success' });
        setShowNewFileModal(false);
        setNewFileName('');
        fetchFiles(currentPath);
        
        // Open the new file for editing
        setTimeout(() => {
          handleEditFile({
            path: filePath,
            is_directory: false,
            name: newFileName
          });
        }, 500);
      } else {
        setMessage({ text: 'Failed to create file', type: 'danger' });
      }
    } catch (err) {
      setMessage({ text: 'Failed to create file', type: 'danger' });
    }
  };

  // Create new folder
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    
    try {
      const response = await createDirectory(newFolderName, currentPath);
      
      if (response) {
        setMessage({ text: 'Folder created successfully', type: 'success' });
        setShowNewFolderModal(false);
        setNewFolderName('');
        fetchFiles(currentPath);
      } else {
        setMessage({ text: 'Failed to create folder', type: 'danger' });
      }
    } catch (err) {
      setMessage({ text: 'Failed to create folder', type: 'danger' });
    }
  };

  // Process dropped items (files and directories)
  const handleDroppedItems = async (items) => {
    const filesAndEntries = [];
    let totalFiles = 0;
    
    // Collect all files and directory entries
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        const entry = item.webkitGetAsEntry ? item.webkitGetAsEntry() : null;
        if (entry) {
          filesAndEntries.push(entry);
          if (entry.isFile) {
            totalFiles++;
          } else if (entry.isDirectory) {
            // We'll count directory files during processing
            totalFiles += 1; // Just for now, will be updated
          }
        } else {
          // Fallback for browsers that don't support webkitGetAsEntry
          const file = item.getAsFile();
          if (file) {
            filesAndEntries.push(file);
            totalFiles++;
          }
        }
      }
    }
    
    if (totalFiles === 0) return;
    
    // Initialize progress tracking
    setUploadProgress({ show: true, percent: 0, processed: 0, total: totalFiles });
    
    // Process files and directories
    await processEntriesRecursively(filesAndEntries, '');
    
    // Refresh file list when done
    setTimeout(() => {
      setUploadProgress({ show: false, percent: 0, processed: 0, total: 0 });
      fetchFiles(currentPath);
      setMessage({ text: 'Bulk upload completed successfully', type: 'success' });
    }, 1000);
  };
  
  // Process entries recursively
  const processEntriesRecursively = async (entries, relativePath) => {
    for (const entry of entries) {
      if (entry && entry.isFile) {
        // It's a FileSystemFileEntry
        await processFileEntry(entry, relativePath);
      } else if (entry && entry.isDirectory) {
        // It's a FileSystemDirectoryEntry
        await processDirectoryEntry(entry, relativePath);
      } else if (entry instanceof File) {
        // It's a regular File object
        await uploadSingleFile(entry, relativePath ? `${currentPath}/${relativePath}` : currentPath);
        updateUploadProgress();
      }
    }
  };
  
  // Process a file entry
  const processFileEntry = (fileEntry, relativePath) => {
    return new Promise((resolve) => {
      fileEntry.file(async (file) => {
        // Directory where the file should be uploaded
        const uploadDir = relativePath ? `${currentPath}/${relativePath}` : currentPath;
        
        try {
          await uploadSingleFile(file, uploadDir);
          updateUploadProgress();
        } catch (error) {
          console.error(`Error uploading file ${file.name}:`, error);
        }
        
        resolve();
      });
    });
  };
  
  // Process a directory entry
  const processDirectoryEntry = async (dirEntry, relativePath) => {
    // Path for new directory relative to current path
    const dirRelativePath = relativePath ? `${relativePath}/${dirEntry.name}` : dirEntry.name;
    const newDirPath = currentPath ? `${currentPath}/${dirEntry.name}` : dirEntry.name;
    
    // Create the directory first
    try {
      if (relativePath) {
        // If there's a relative path, create the full path
        await createDirectory(dirEntry.name, `${currentPath}/${relativePath}`);
      } else {
        // Otherwise create it directly in the current path
        await createDirectory(dirEntry.name, currentPath);
      }
    } catch (error) {
      console.error(`Error creating directory ${dirEntry.name}:`, error);
    }
    
    // Read directory contents
    const entries = await readDirectoryEntries(dirEntry);
    
    // Process all entries in the directory
    await processEntriesRecursively(entries, dirRelativePath);
  };
  
  // Read all entries in a directory
  const readDirectoryEntries = (dirEntry) => {
    return new Promise((resolve) => {
      const reader = dirEntry.createReader();
      const entries = [];
      
      function readEntries() {
        reader.readEntries((results) => {
          if (results.length) {
            entries.push(...results);
            readEntries(); // Continue reading until empty
          } else {
            resolve(entries);
          }
        }, (error) => {
          console.error('Error reading directory entries:', error);
          resolve(entries);
        });
      }
      
      readEntries();
    });
  };
  
  // Create a directory
  const createDirectory = async (name, parentDir) => {
    const dirPath = parentDir 
      ? `${parentDir}/${name}`
      : name;
    
    try {
      await axios.post(`/api/sites/${siteId}/directories/${targetQuery}`, {
        directory_name: dirPath
      });
      return true;
    } catch (error) {
      // Ignore "already exists" errors
      if (error.response && error.response.status === 409) {
        return true;
      }
      throw error;
    }
  };
  
  // Update progress counter
  const updateUploadProgress = () => {
    setUploadProgress(prev => {
      const processed = prev.processed + 1;
      const percent = Math.floor((processed / prev.total) * 100);
      return { ...prev, processed, percent };
    });
  };
  
  // Upload a single file - updated to use the new API structure
  // Upload a single file - updated to handle 413 errors
  const uploadSingleFile = async (file, directory) => {
    // Check file size before uploading (100MB limit)
    const MAX_FILE_SIZE = 1024 * 1024 * 1024; // 100MB in bytes
  
    if (file.size > MAX_FILE_SIZE) {
      setMessage({ 
        text: `File "${file.name}" exceeds the 100MB size limit. Please use a smaller file.`, 
        type: 'danger' 
      });
      return false;
    }
  
    const formData = new FormData();
    formData.append('file', file);
    formData.append('filename', file.name);
  
    try {
      // New API structure - pass directory and filename separately
      await axios.post(
        `/api/sites/${siteId}/upload/${targetQuery}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          params: { directory: directory || '' }
        }
      );
      return true;
    } catch (error) {
      // Handle specific error codes
      if (error.response) {
        if (error.response.status === 413) {
          setMessage({ 
            text: `File "${file.name}" is too large. Maximum file size is 100MB.`, 
            type: 'danger' 
          });
        } else {
          setMessage({ 
            text: `Error uploading "${file.name}": ${error.response.data || error.message}`, 
            type: 'danger' 
          });
        }
      } else {
        setMessage({ 
          text: `Network error while uploading "${file.name}". Please try again.`, 
          type: 'danger' 
        });
      }
      console.error(`Error uploading file ${file.name} to ${directory}:`, error);
      return false;
    }
  };

  // Handle file upload from input element
  const handleFileUpload = (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    handleFiles(Array.from(files));
  };
  
  // Handle multiple file uploads
  // Handle multiple file uploads
  const handleFiles = async (files) => {
    // Check for any files that exceed the size limit
    const MAX_FILE_SIZE = 1024 * 1024 * 1024; // 1Gb
    const oversizedFiles = Array.from(files).filter(file => file.size > MAX_FILE_SIZE);
  
    if (oversizedFiles.length > 0) {
      const fileList = oversizedFiles.map(f => `"${f.name}"`).join(", ");
      setMessage({ 
        text: `The following files exceed the 1Gb size limit: ${fileList}`, 
        type: 'danger' 
      });
    
      // If all files are too large, return early
      if (oversizedFiles.length === files.length) {
        return;
      }
    }
  
    // Only process files under the size limit
    const validFiles = Array.from(files).filter(file => file.size <= MAX_FILE_SIZE);
  
    setUploadProgress({ show: true, percent: 0, processed: 0, total: validFiles.length });
  
    let successCount = 0;
    let errorCount = 0;
  
    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
    
      // Preserve directory structure if available (from directory input)
      let relativePath = '';
      if (file.webkitRelativePath) {
        // If the file has a relative path (from directory upload), extract the directory part
        const pathParts = file.webkitRelativePath.split('/');
        pathParts.pop(); // Remove filename
        relativePath = pathParts.join('/');
      
        // Create necessary directories
        if (relativePath) {
          const fullDirPath = currentPath ? `${currentPath}/${relativePath}` : relativePath;
          await createDirectory(fullDirPath, '');
        }
      }
    
      const uploadDir = relativePath ? 
        (currentPath ? `${currentPath}/${relativePath}` : relativePath) : 
        currentPath;
    
      const success = await uploadSingleFile(file, uploadDir);
      if (success) {
        successCount++;
      } else {
        errorCount++;
      }
      updateUploadProgress();
    }
  
    // Refresh the file list
    setTimeout(() => {
      setUploadProgress({ show: false, percent: 0, processed: 0, total: 0 });
      fetchFiles(currentPath);
    
      // Show appropriate success/error message
      if (errorCount === 0) {
        setMessage({ text: 'Files uploaded successfully', type: 'success' });
      } else if (successCount === 0) {
        // Message already set by uploadSingleFile
      } else {
        setMessage({ 
          text: `Upload completed with ${successCount} successful and ${errorCount} failed files`, 
          type: 'warning' 
        });
      }
    
      // Clear the file input
      if (fileInputRef.current) fileInputRef.current.value = '';
    }, 1000);
  };
  // Handle file deletion
  const handleDelete = (file) => {
    setFileToDelete(file);
    setShowDeleteConfirm(true);
  };
  
  // Confirm deletion
  const confirmDelete = async () => {
    if (!fileToDelete) return;
    
    try {
      const response = await deleteFile(siteId, fileToDelete.path, fileToDelete.is_directory, target);
      
      if (response.success) {
        setMessage({ text: response.message || 'Item deleted successfully', type: 'success' });
        fetchFiles(currentPath);
      } else {
        setMessage({ text: response.message || 'Failed to delete item', type: 'danger' });
      }
    } catch (err) {
      setMessage({ text: 'Failed to delete item', type: 'danger' });
    } finally {
      setShowDeleteConfirm(false);
      setFileToDelete(null);
    }
  };

  const getFileName = (path) => path.split('/').pop();
  
  const encodePath = (path) => {
    return path.split('/').map(encodeURIComponent).join('/');
  };

  // Build breadcrumb navigation
  const breadcrumbItems = [];
  breadcrumbItems.push(
    <Breadcrumb.Item 
      key="root" 
      onClick={() => handleNavigate('')}
      active={currentPath === ''}
    >
      Root
    </Breadcrumb.Item>
  );
  
  if (currentPath) {
    const pathParts = currentPath.split('/');
    let cumulativePath = '';
    pathParts.forEach((part, index) => {
      cumulativePath = cumulativePath ? `${cumulativePath}/${part}` : part;
      breadcrumbItems.push(
        <Breadcrumb.Item 
          key={cumulativePath}
          onClick={() => handleNavigate(cumulativePath)}
          active={index === pathParts.length - 1}
        >
          {part}
        </Breadcrumb.Item>
      );
    });
  }

  const downloadableFiles = files.filter((file) => !file.is_directory);

  if (loading && files.length === 0) return <div>Loading...</div>;
  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>{staging ? 'Staging files' : 'File Browser'}</h1>
        <Button 
          variant="outline-primary" 
          onClick={navigateToSite}
        >
          <i className="bi bi-arrow-left"></i> Back to Site
        </Button>
      </div>
      
      {staging && <Alert variant="info">You are editing staging. These changes only reach the live website when you publish.</Alert>}
      {message && (
        <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* Simplified upload UI */}
      <div className="mb-3 d-flex flex-wrap">
        <Button 
          variant="primary" 
          className="me-2 mb-2"
          onClick={() => setShowNewFileModal(true)}
        >
          New File
        </Button>
        <Button 
          variant="success" 
          className="me-2 mb-2"
          onClick={() => setShowNewFolderModal(true)}
        >
          New Folder
        </Button>
        <DropdownButton
          id="download-dropdown"
          title="Download"
          variant="success"
          className="me-2 mb-2 download-dropdown"
        >
          <Dropdown.Item
            onClick={handleDownloadAll}
            disabled={downloadableFiles.length === 0}
          >
            Download Directory
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item onClick={handleDownloadEntireSite}>
            Download Site
          </Dropdown.Item>
        </DropdownButton>
        {/* File input for uploads */}
        <input 
          type="file" 
          ref={fileInputRef}
          className="d-none"
          onChange={handleFileUpload}
          multiple
        />
        <Button
          variant="outline-primary"
          className="me-2 mb-2"
          onClick={() => fileInputRef.current?.click()}
        >
          Upload Files
        </Button>
      </div>

      {/* Upload Progress Bar */}
      {uploadProgress.show && (
        <div className="mb-3">
          <ProgressBar 
            now={uploadProgress.percent} 
            label={`${uploadProgress.processed}/${uploadProgress.total} (${uploadProgress.percent}%)`} 
            animated
          />
        </div>
      )}

      {/* Enhanced Drag & Drop Zone with better guidance */}
      <div 
        ref={dropZoneRef}
        className="mb-3 p-4 text-center"
        style={{
          border: `2px dashed ${isDragging ? '#0d6efd' : '#dee2e6'}`,
          borderRadius: '4px',
          backgroundColor: isDragging ? 'rgba(13, 110, 253, 0.05)' : 'transparent',
          transition: 'all 0.2s'
        }}
      >
        {isDragging ? (
          <div>
            <h5>Drop to upload</h5>
            <p className="mb-0">Files and folders will be uploaded to the current directory: {currentPath || 'Root'}</p>
          </div>
        ) : (
          <div>
            <h5>Drag and drop files or folders here</h5>
            <p className="mb-0">Files will be uploaded to: {currentPath || 'Root'}</p>
          </div>
        )}
      </div>

      <Breadcrumb className="mb-3">
        {breadcrumbItems}
      </Breadcrumb>

      <Table responsive striped bordered hover>
        <thead>
          <tr>
            <th>Name</th>
            <th>Size</th>
            <th>Last Modified</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {/* Parent directory link if we're in a subdirectory */}
          {currentPath && (
            <tr>
              <td colSpan="3">
                <Button 
                  variant="link" 
                  className="p-0"
                  onClick={() => {
                    const parentPath = currentPath.split('/').slice(0, -1).join('/');
                    handleNavigate(parentPath);
                  }}
                >
                  <i className="bi bi-arrow-up"></i> Parent Directory
                </Button>
              </td>
              <td></td>
            </tr>
          )}

          {files.length === 0 ? (
            <tr>
              <td colSpan="4" className="text-center">No files found</td>
            </tr>
          ) : (
            files.map((file, index) => (
              <tr key={index}>
                <td>
                  {file.is_directory ? (
                    <Button 
                      variant="link" 
                      className="p-0" 
                      onClick={() => handleNavigate(file.path)}
                    >
                      📁 {getFileName(file.path)}
                    </Button>
                  ) : (
                    <span>📄 {getFileName(file.path)}</span>
                  )}
                </td>
                <td>{file.is_directory ? '-' : formatBytes(file.size)}</td>
                <td>{file.last_modified || '-'}</td>
                <td>
                  <div className="d-flex">
                    {!file.is_directory && (<>
                      <Button
                        variant="primary"
                        size="sm"
                        className="me-2"
                        onClick={() => handleEditFile(file)}
                      >
                        View/Edit
                      </Button>
                      <Button
                        variant="info"
                        size="sm"
                        className="me-2"
                        onClick={() => handleDownload(file)}
                      >
                        Download
                      </Button>
                    </>)}
                    <Button 
                      variant="danger" 
                      size="sm"
                      onClick={() => handleDelete(file)}
                    >
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {/* Edit File Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Edit File: {editingFile && getFileName(editingFile.path)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {contentLoading ? (
            <div className="text-center p-4">
              <Spinner animation="border" />
              <p className="mt-2">Loading file content...</p>
            </div>
          ) : (
            <Form.Control
              as="textarea"
              rows={15}
              value={fileContent}
              onChange={(e) => setFileContent(e.target.value)}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveFile} disabled={contentLoading}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Image Viewer Modal */}
      <Modal show={showImageViewer} onHide={() => setShowImageViewer(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            View Image: {editingFile && getFileName(editingFile.path)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {editingFile && (
            <img 
              src={`/api/sites/${siteId}/download/${encodePath(editingFile.path)}/${targetQuery}`}
              alt={getFileName(editingFile.path)}
              className="img-fluid"
              style={{ maxHeight: '70vh' }}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* New File Modal */}
      <Modal show={showNewFileModal} onHide={() => setShowNewFileModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create New File</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>File Name</Form.Label>
            <Form.Control
              type="text"
              value={newFileName}
              onChange={(e) => setNewFileName(e.target.value)}
              placeholder="Enter file name (e.g. index.html)"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNewFileModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleCreateFile}
            disabled={!newFileName.trim()}
          >
            Create
          </Button>
        </Modal.Footer>
      </Modal>

      {/* New Folder Modal */}
      <Modal show={showNewFolderModal} onHide={() => setShowNewFolderModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Folder</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Folder Name</Form.Label>
            <Form.Control
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Enter folder name (e.g. images)"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNewFolderModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="success" 
            onClick={handleCreateFolder}
            disabled={!newFolderName.trim()}
          >
            Create Folder
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {fileToDelete && (
            <>
              <p>
                Are you sure you want to delete{' '}
                <strong>
                  {fileToDelete.is_directory ? 'directory' : 'file'}{' '}
                  "{getFileName(fileToDelete.path)}"
                </strong>
                ?
              </p>
              {fileToDelete.is_directory && (
                <Alert variant="warning">
                  This will delete the directory and all its contents!
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={confirmDelete}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

// Helper function to format file sizes
function formatBytes(bytes) {
  if (bytes === 0 || bytes === null) return '-';
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
}

export default FileBrowser;
